import React from 'react';

/**
 * Espejo de `ETIQUETAS_DESC_NEGRITA` del backend (SPEC-155 · RN-004).
 * Debe permanecer sincronizado con
 * `cv-printing-backend/src/modules/cotizaciones/services/cotizacion-pdf.template.ts`.
 */
export const ETIQUETAS_DESC_NEGRITA = [
  'Material:',
  'Medidas:',
  'Área de impresión:',
  'Marcación:',
] as const;

/**
 * Parte la descripción en líneas replicando la semántica de `renderDescripcion`
 * del PDF (SPEC-155 · RN-003): colapsa 2+ saltos consecutivos a uno y detecta la
 * etiqueta de sección solo cuando ABRE la línea.
 *
 * Función pura y sin JSX → fácil de testear (T-005).
 */
export function parseDescripcionSecciones(
  descripcion: string,
): Array<{ etiqueta: string | null; resto: string }> {
  // 1) colapsar 2+ saltos de línea consecutivos a uno solo (RN-003 / CA-006)
  const texto = descripcion.replace(/\n{2,}/g, '\n');
  // 2) partir en líneas; resaltar la etiqueta solo si ABRE la línea (RN-003 / CA-007)
  return texto.split('\n').map(linea => {
    const etiqueta = ETIQUETAS_DESC_NEGRITA.find(e => linea.startsWith(e)) ?? null;
    const resto = etiqueta ? linea.slice(etiqueta.length) : linea;
    return { etiqueta, resto };
  });
}

/**
 * Renderiza la descripción con el mismo formato del PDF (SPEC-155 · RN-003/007):
 * etiquetas de sección en <strong> solo al inicio de línea, saltos preservados
 * mediante `\n` literales + `whiteSpace: pre-line` en el contenedor.
 *
 * Devuelve nodos React (texto auto-escapado) → XSS-safe: cualquier `<`, `>`, `&`
 * del texto se muestra literal (RN-007 / CA-008). No usa `dangerouslySetInnerHTML`.
 */
export function formatDescripcionSecciones(descripcion: string): React.ReactNode {
  const lineas = parseDescripcionSecciones(descripcion);
  return lineas.map(({ etiqueta, resto }, i) => (
    <React.Fragment key={i}>
      {etiqueta ? (
        <>
          <strong>{etiqueta}</strong>
          {resto}
        </>
      ) : (
        resto
      )}
      {i < lineas.length - 1 ? '\n' : ''}
    </React.Fragment>
  ));
}
